import * as React from "react";
import { clsx } from "clsx";
import { MediaQuery } from "react-responsive";
import { isFocusable, tabbable } from "tabbable";
import { Accordion } from "../../accordion/accordion/accordion.mjs";
import { Button } from "../../button/button.mjs";
import { Heading } from "../../typography/heading/heading.mjs";
import { Modal } from "../../modal/modal/modal.mjs";
import { ModalContent } from "../../modal/modal-content/modal-content.mjs";
import { ModalFooter } from "../../modal/modal-footer/modal-footer.mjs";
import { ModalHeader } from "../../modal/modal-header/modal-header.mjs";
import { Skeleton } from "../../skeleton/skeleton/skeleton.mjs";
import { SkeletonItem } from "../../skeleton/skeleton-item/skeleton-item.mjs";
import { ThemeContext } from "../../helpers/theme-provider/theme-provider.mjs";
import { FilterHeader } from "../filter-header/filter-header.mjs";
import { FilterContext } from "./filter-context.mjs";
const _Filter = class _Filter extends React.Component {
  constructor(props) {
    super(props);
    this.filter = null;
    this.handleResize = () => {
      const isLargeScreen = window.matchMedia("(min-width: ".concat(this.context.media.width.md.min, ")")).matches;
      if (isLargeScreen && this.props.isOpen) {
        this.closeModal();
      } else if (!isLargeScreen && this.props.isOpen) {
        this.openModal();
      }
    };
    this.restoreFocus = () => {
      requestAnimationFrame(() => {
        if (this.state.elementToRestoreFocus && isFocusable(this.state.elementToRestoreFocus)) {
          this.state.elementToRestoreFocus.focus();
        }
      });
    };
    this.handleCloseClick = (event) => {
      event.preventDefault();
      if (this.props.onToggle) {
        this.props.onToggle(false);
      }
    };
    this.isFilterSectionHeader = (element) => {
      return !!element && !!element.className && element.className.includes("accordion__title");
    };
    this.onKeyDown = (event) => {
      const componentRef = this.filter;
      if (componentRef) {
        const tabbableElements = tabbable(componentRef);
        const filterSectionHeaders = tabbableElements.filter((e) => this.isFilterSectionHeader(e));
        if (filterSectionHeaders.length) {
          const firstSectionHeader = filterSectionHeaders[0];
          const lastSectionHeader = filterSectionHeaders[filterSectionHeaders.length - 1];
          const activeSectionHeader = filterSectionHeaders.find((e) => e === document.activeElement);
          switch (event.key) {
            case "ArrowDown":
            case "Down":
              if (document.activeElement === lastSectionHeader) {
                event.preventDefault();
                firstSectionHeader.focus();
              } else if (activeSectionHeader) {
                event.preventDefault();
                const focusedElementIndex = filterSectionHeaders.indexOf(activeSectionHeader);
                filterSectionHeaders[focusedElementIndex + 1].focus();
              }
              break;
            case "Up":
            case "ArrowUp":
              if (document.activeElement === firstSectionHeader) {
                event.preventDefault();
                lastSectionHeader.focus();
              } else if (activeSectionHeader) {
                event.preventDefault();
                const focusedElementIndex = filterSectionHeaders.indexOf(activeSectionHeader);
                filterSectionHeaders[focusedElementIndex - 1].focus();
              }
              break;
            case "End":
              if (activeSectionHeader) {
                event.preventDefault();
                lastSectionHeader.focus();
              }
              break;
            case "Home":
              if (activeSectionHeader) {
                event.preventDefault();
                firstSectionHeader.focus();
              }
              break;
          }
        }
      }
    };
    this.state = {
      isModalOpen: false,
      elementToRestoreFocus: null,
      isMounted: false
    };
  }
  componentDidMount() {
    const isLargeScreen = window.matchMedia("(min-width: ".concat(this.context.media.width.md.min, ")")).matches;
    this.setState({ isMounted: true });
    if (this.props.isOpen && !isLargeScreen) {
      this.openModal();
    }
    window.addEventListener("resize", this.handleResize);
  }
  componentDidUpdate(prevProps) {
    const isLargeScreen = window.matchMedia("(min-width: ".concat(this.context.media.width.md.min, ")")).matches;
    if (this.props.isOpen && !prevProps.isOpen && !isLargeScreen) {
      this.openModal();
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      if (!isLargeScreen) {
        this.closeModal(this.restoreFocus);
      } else {
        this.closeModal();
      }
    }
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }
  initFocus() {
    this.setState({ elementToRestoreFocus: document.activeElement });
    requestAnimationFrame(() => {
      if (this.filter) {
        const firstTabbableElement = tabbable(this.filter).find(this.isFilterSectionHeader);
        if (firstTabbableElement) {
          firstTabbableElement.focus();
          return;
        }
        this.filter.focus();
      }
    });
  }
  openModal() {
    if (!this.state.isModalOpen) {
      this.setState(
        {
          isModalOpen: true
        },
        this.initFocus
      );
    }
  }
  closeModal(callback = () => void 0) {
    if (this.state.isModalOpen) {
      this.setState(
        {
          isModalOpen: false
        },
        callback
      );
    }
  }
  renderSaveButton(button) {
    return /* @__PURE__ */ React.createElement(Button, { ...button, type: this.props.purpose === "employee" ? "employee" : void 0, block: true, className: clsx("filter__save", button.className) });
  }
  renderClearButton(button) {
    return /* @__PURE__ */ React.createElement(Button, { link: true, ...button, type: this.props.purpose === "employee" ? "employee" : void 0 });
  }
  renderSkeleton(props) {
    const { skeleton } = props;
    return /* @__PURE__ */ React.createElement(Skeleton, { className: "filter", label: skeleton.label }, /* @__PURE__ */ React.createElement(SkeletonItem, { className: "h2 filter__heading", width: "60%" }), /* @__PURE__ */ React.createElement(SkeletonItem, { height: "calc(100vh - 87px)" }));
  }
  renderHeading() {
    var _a, _b, _c;
    const headingLevel = (_b = (_a = this.props.heading) == null ? void 0 : _a.level) != null ? _b : 1;
    const headingClassName = ((_c = this.props.heading) == null ? void 0 : _c.className) || "h4";
    if (this.props.heading) {
      const { className, level, ...rest } = this.props.heading;
      return /* @__PURE__ */ React.createElement(Heading, { level: headingLevel, className: clsx("filter__heading", headingClassName), ...rest }, this.props.title);
    }
    return /* @__PURE__ */ React.createElement(Heading, { level: headingLevel, className: clsx("filter__heading", headingClassName) }, this.props.title);
  }
  renderFilter() {
    var _a, _b;
    if ("skeleton" in this.props) {
      return this.renderSkeleton(this.props);
    }
    const headingLevel = ((_b = (_a = this.props.heading) == null ? void 0 : _a.level) != null ? _b : 1) + 1;
    const childrenArray = React.Children.toArray(this.props.children);
    const filteredHeader = childrenArray.find((child) => React.isValidElement(child) && child.type === FilterHeader);
    const filteredChildren = childrenArray.filter((child) => !React.isValidElement(child) || child.type !== FilterHeader);
    return /* @__PURE__ */ React.createElement(FilterContext.Provider, { value: { purpose: this.props.purpose || "default" } }, /* @__PURE__ */ React.createElement("div", { className: "filter", ref: (element) => this.filter = element, onKeyDown: this.onKeyDown }, /* @__PURE__ */ React.createElement("div", { className: "filter__main" }, this.renderHeading(), filteredHeader, /* @__PURE__ */ React.createElement(Accordion, { headingLevel }, filteredChildren)), !this.state.isModalOpen && /* @__PURE__ */ React.createElement("div", { className: "filter__footer" }, this.props.saveButton && this.renderSaveButton(this.props.saveButton), this.props.clearButton && this.renderClearButton(this.props.clearButton))));
  }
  renderFilterInModal() {
    const firstFocusableEl = this.filter && tabbable(this.filter)[0];
    return /* @__PURE__ */ React.createElement(
      Modal,
      {
        isOpen: this.props.isOpen,
        isFullScreen: true,
        isClosable: "backdrop",
        onClose: this.handleCloseClick,
        initialFocusedElementId: firstFocusableEl == null ? void 0 : firstFocusableEl.id
      },
      /* @__PURE__ */ React.createElement(
        ModalHeader,
        {
          backButton: {
            icon: "#arrow-left",
            text: "Tagasi",
            className: "filter__close",
            onClick: this.handleCloseClick
          }
        },
        /* @__PURE__ */ React.createElement("div", { className: "text-right" }, this.props.clearButton && /* @__PURE__ */ React.createElement(
          Button,
          {
            link: true,
            onClick: this.handleCloseClick,
            ...this.props.clearButton,
            type: this.props.purpose === "employee" ? "employee" : void 0
          }
        ))
      ),
      /* @__PURE__ */ React.createElement(ModalContent, null, this.renderFilter()),
      this.props.saveButton && /* @__PURE__ */ React.createElement(ModalFooter, { isSticky: true }, this.renderSaveButton(this.props.saveButton))
    );
  }
  render() {
    return /* @__PURE__ */ React.createElement(MediaQuery, { minWidth: this.context.media.width.md.min }, (matches) => matches ? this.renderFilter() : this.renderFilterInModal());
  }
};
_Filter.contextType = ThemeContext;
let Filter = _Filter;
export {
  Filter
};
